import { createNavioClient } from '@navio-maritime/platform/client';
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const productUrl = import.meta.env.VITE_SUPABASE_URL;
const productAnon = import.meta.env.VITE_SUPABASE_ANON_KEY;
const navioUrl = import.meta.env.VITE_NAVIO_URL;
const navioAnon = import.meta.env.VITE_NAVIO_ANON_KEY;

export const NAVIO_AUTH_MODE = !!(navioUrl && navioAnon);

if (!productUrl || !productAnon) {
  console.warn('VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set — auth will not work');
}

const GLOBAL_KEY_DATA = '__navioworks_build_supabase_client__' as const;
const GLOBAL_KEY_AUTH = '__navioworks_build_supabase_auth_client__' as const;

interface ClientPair {
  data: SupabaseClient;
  auth: SupabaseClient;
}

function getOrCreateClients(): ClientPair {
  const existingData = (globalThis as Record<string, unknown>)[GLOBAL_KEY_DATA] as
    | SupabaseClient
    | undefined;
  const existingAuth = (globalThis as Record<string, unknown>)[GLOBAL_KEY_AUTH] as
    | SupabaseClient
    | undefined;
  if (existingData && existingAuth) {
    return { data: existingData, auth: existingAuth };
  }

  if (NAVIO_AUTH_MODE) {
    const { authClient, dataClient } = createNavioClient({
      navioUrl: navioUrl!,
      navioAnon: navioAnon!,
      productUrl: productUrl || 'http://localhost',
      productAnon: productAnon || 'placeholder',
      authClientOptions: {
        auth: {
          lock: async <R>(_n: string, _t: number, fn: () => Promise<R>): Promise<R> => fn(),
        },
      },
    });
    (globalThis as Record<string, unknown>)[GLOBAL_KEY_DATA] = dataClient;
    (globalThis as Record<string, unknown>)[GLOBAL_KEY_AUTH] = authClient;
    return { data: dataClient, auth: authClient };
  }

  const client = createClient(
    productUrl || 'http://localhost',
    productAnon || 'placeholder',
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        lock: async <R>(_n: string, _t: number, fn: () => Promise<R>): Promise<R> => fn(),
      },
    },
  );

  (globalThis as Record<string, unknown>)[GLOBAL_KEY_DATA] = client;
  (globalThis as Record<string, unknown>)[GLOBAL_KEY_AUTH] = client;
  return { data: client, auth: client };
}

const clients = getOrCreateClients();

/**
 * Data-side Supabase client. In NAVIO_AUTH_MODE, points at navioworks-staging /
 * navioworks-prod and uses an `accessToken` callback that exchanges the
 * navio-platform JWT via `auth-exchange`. In legacy mode, the single client.
 *
 * Use for `.from(...)`, `.storage`, `.rpc(...)`, `.functions.invoke(...)`.
 * Do NOT use `supabase.auth.*` in NAVIO_AUTH_MODE — use `supabaseAuth`.
 */
export const supabase = clients.data;

/**
 * Auth-side Supabase client. In NAVIO_AUTH_MODE, points at navio-platform.
 * Use for sign-in / sign-up / sign-out / session / MFA.
 */
export const supabaseAuth = clients.auth;
